import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/layout';
import { ChatList, FilterChips } from '@/components/domain';
import { EmptyState, ErrorState, Spinner } from '@/components/atoms';
import { useChatsStore } from '@/store';
import { chats as chatsApi } from '@/api';
import { logger } from '@/utils/logger';
import type { ChatSummary, OrderStatus } from '@/types/domain';

// F-170 B2.9 — unified Chats/Orders inbox. "Усі" = active + completed
// (no archive); other tabs filter the same store-backed dataset.
type Tab = 'all' | 'active' | 'completed' | 'archived';

const TAB_OPTIONS: { value: Tab; label: string }[] = [
  { value: 'all', label: 'Усі' },
  { value: 'active', label: 'Активні' },
  { value: 'completed', label: 'Завершені' },
  { value: 'archived', label: 'Архів' },
];

const TERMINAL_STATUSES: OrderStatus[] = ['completed', 'reviewed', 'cancelled'];

// F-180 C3: a chat counts as "completed" for the tab filter when EITHER the
// historical `has_completed_order` flag is set (chat once had a completed
// order, even if a new active one replaced it) OR the currently active order
// itself is in a terminal state. The active-order check stays as the fallback
// for the cancelled / reviewed legs that the backend flag intentionally
// ignores.
function isCompleted(chat: ChatSummary): boolean {
  if (chat.has_completed_order) return true;
  const status = chat.active_order?.status;
  return !!status && TERMINAL_STATUSES.includes(status);
}

export function ChatListPage(): JSX.Element {
  // Live (non-archived) chats come from the shared store so WS events keep
  // them current; archived chats are fetched on-demand because they don't
  // get realtime updates.
  const storeChats = useChatsStore((s) => s.chats);
  const setStoreChats = useChatsStore((s) => s.setChats);
  const [tab, setTab] = useState<Tab>('active');
  const [archivedChats, setArchivedChats] = useState<ChatSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadActive = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatsApi.fetchChats({ archived: false });
      setStoreChats(data.results);
    } catch (err) {
      logger.warn('Chats fetch failed', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [setStoreChats]);

  const reloadArchived = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatsApi.fetchChats({ archived: true });
      setArchivedChats(data.results);
    } catch (err) {
      logger.warn('Archived chats fetch failed', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'archived') {
      void reloadArchived();
    } else {
      void reloadActive();
    }
  }, [tab, reloadActive, reloadArchived]);

  const handleArchive = useCallback(
    async (chatId: number) => {
      try {
        await chatsApi.archiveChat(chatId);
        setStoreChats(storeChats.filter((c) => c.id !== chatId));
      } catch (err) {
        logger.warn('Archive chat failed', err);
      }
    },
    [storeChats, setStoreChats],
  );

  const handleUnarchive = useCallback(
    async (chatId: number) => {
      try {
        await chatsApi.unarchiveChat(chatId);
        setArchivedChats((prev) => prev.filter((c) => c.id !== chatId));
      } catch (err) {
        logger.warn('Unarchive chat failed', err);
      }
    },
    [],
  );

  const chats = useMemo<ChatSummary[]>(() => {
    if (tab === 'archived') return archivedChats;
    if (tab === 'all') return storeChats;
    if (tab === 'completed') return storeChats.filter(isCompleted);
    // active: chat without a closed order OR with an open status
    return storeChats.filter((c) => !isCompleted(c));
  }, [tab, storeChats, archivedChats]);

  const emptyTitle =
    tab === 'archived'
      ? 'Архів порожній'
      : tab === 'completed'
        ? 'Поки нічого не завершено'
        : 'Поки тиша';
  const emptyMessage =
    tab === 'archived'
      ? 'Сюди потраплять чати, які ти заархівуєш.'
      : tab === 'completed'
        ? 'Завершені замовлення зʼявляться тут.'
        : "Як зʼявиться повідомлення — пушу. Поки шукай майстрів.";

  return (
    <Layout title="Чати">
      <FilterChips
        options={TAB_OPTIONS}
        selected={[tab]}
        onChange={(next) => {
          const picked = (next[0] as Tab) ?? 'active';
          setTab(picked);
        }}
        multi={false}
      />
      {error ? (
        <ErrorState
          onRetry={() =>
            tab === 'archived' ? void reloadArchived() : void reloadActive()
          }
        />
      ) : loading ? (
        <Spinner center />
      ) : chats.length === 0 ? (
        <EmptyState
          icon="chats"
          title={emptyTitle}
          message={emptyMessage}
          action={
            tab === 'archived' || tab === 'completed'
              ? undefined
              : { label: 'Знайти спеціаліста', to: '/search' }
          }
        />
      ) : (
        <ChatList
          chats={chats}
          onArchive={tab !== 'archived' ? handleArchive : undefined}
          onUnarchive={tab === 'archived' ? handleUnarchive : undefined}
        />
      )}
    </Layout>
  );
}
